import {
  getWorldMapObject,
  ShapeType,
  WORLD_MAP_OBJECTS,
  type WorldMapKind,
} from '@blocksuite/affine-model';
import {
  EdgelessToolbarToolMixin,
  SeniorToolExtension,
} from '@blocksuite/affine-widget-edgeless-toolbar';
import { SignalWatcher } from '@blocksuite/global/lit';
import { css, html, LitElement, svg } from 'lit';
import { property, state } from 'lit/decorators.js';

import { WorldMapTool } from '../shape-tool';

function icon(kind: WorldMapKind) {
  return svg`<svg viewBox="0 0 64 64" aria-hidden="true" fill="none"
    stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
    ${getWorldMapObject(kind)?.paths.map(part => svg`<path d=${part.d}></path>`)}
  </svg>`;
}

export class EdgelessWorldMapMenu extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }
    .objects {
      display: grid;
      grid-template-columns: repeat(3, 80px);
      align-items: center;
      gap: 4px;
      padding: 8px;
    }
    button {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 4px;
      width: 80px;
      height: 66px;
      border: 0;
      border-radius: 8px;
      background: transparent;
      color: var(--affine-icon-color);
      cursor: pointer;
      font: inherit;
    }
    button:hover,
    button:focus-visible {
      background: var(--affine-hover-color);
    }
    button:focus-visible {
      outline: 2px solid var(--affine-primary-color);
    }
    svg {
      width: 32px;
      height: 32px;
    }
    span {
      font-size: 11px;
      white-space: nowrap;
    }
  `;
  @property({ attribute: false })
  accessor onPick: (kind: WorldMapKind) => void = () => {};
  override render() {
    return html`<edgeless-slide-menu
      .height=${Math.ceil(WORLD_MAP_OBJECTS.length / 3) * 70 + 12 + 'px'}
      ><div class="objects" role="group" aria-label="World Map objects">
        ${WORLD_MAP_OBJECTS.map(
          object => html`<button
            type="button"
            aria-label=${object.name}
            title=${object.name}
            data-world-map-kind=${object.kind}
            @click=${() => this.onPick(object.kind)}
          >
            ${icon(object.kind)}<span>${object.name}</span>
          </button>`
        )}
      </div></edgeless-slide-menu
    >`;
  }
}

export class EdgelessWorldMapToolButton extends EdgelessToolbarToolMixin(
  SignalWatcher(LitElement)
) {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
    }
    button {
      width: 100%;
      height: 64px;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 2px;
      background: transparent;
      border: 0;
      cursor: pointer;
      color: var(--affine-icon-color);
      font: inherit;
    }
    button:hover,
    button[aria-expanded='true'] {
      background: var(--affine-hover-color);
      border-radius: 8px;
    }
    button:focus-visible {
      outline: 2px solid var(--affine-primary-color);
      border-radius: 8px;
    }
    svg {
      width: 32px;
      height: 32px;
    }
    span {
      font-size: 11px;
    }
  `;
  override type = WorldMapTool;
  @state()
  accessor selectedKind: WorldMapKind = 'topic';

  private readonly pick = (kind: WorldMapKind) => {
    this.selectedKind = kind;
    this.setEdgelessTool(WorldMapTool, {
      shapeName: ShapeType.Rect,
      worldMapKind: kind,
    });
    const tool = this.gfx.tool.currentTool$.peek();
    if (tool instanceof WorldMapTool) tool.createOverlay();
  };

  private readonly toggle = () => {
    if (this.popper) {
      this.popper.dispose();
      return;
    }
    this.createPopper('edgeless-world-map-menu', this, {
      height: Math.ceil(WORLD_MAP_OBJECTS.length / 3) * 70 + 24 + 'px',
      setProps: menu => {
        menu.onPick = this.pick;
      },
    });
  };

  override render() {
    return html`<button
      type="button"
      aria-label="World Map"
      title="World Map"
      aria-expanded=${!!this.popper}
      data-testid="world-map-tool"
      @click=${this.toggle}
    >
      ${icon(this.selectedKind)}<span>World Map</span>
    </button>`;
  }
}

export const worldMapSeniorTool = SeniorToolExtension(
  'world-map',
  ({ block, toolbarContainer }) => ({
    name: 'World Map',
    content: html`<edgeless-world-map-tool-button
      .edgeless=${block}
      .toolbarContainer=${toolbarContainer}
    ></edgeless-world-map-tool-button>`,
  })
);
